"use client";

import Link from "@/components/ui/LocaleLink";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { stripLocalePrefix } from "@/i18n/routing";

// Parallel to `LoginLink`. Wraps `<Link href="/signup">` and appends
// `?next=<currentPath + search>` so a signup CTA from anywhere in the
// marketing site or a pro profile returns the user to where they were
// after the auth chain completes (signup → email confirm → callback →
// /<original-page>).
//
// `LoginLink` and this component intentionally exist as two thin
// components rather than a single `<AuthLink target="...">` because
// the trigger text varies per call site (Login vs "Create Free Account"
// vs "Create a free account" vs sometimes nothing) and most callers
// would rather pass children than juggle a prop.

const AUTH_ROUTES = ["/login", "/signup", "/reset", "/verify"];

export default function SignupLink({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  // Strip the locale prefix (/vi, /fr) before the auth-page check so VI/FR auth
  // pages are detected; `pathname` itself stays full so `next=` preserves locale.
  const localePath = stripLocalePrefix(pathname);
  const isAuthPage = AUTH_ROUTES.some((r) => localePath.startsWith(r));

  // SSR/initial render: pathname-only (no window.location available).
  // Effect upgrades to pathname+search once client-side.
  const [href, setHref] = useState(
    isAuthPage ? "/signup" : `/signup?next=${encodeURIComponent(pathname)}`,
  );

  useEffect(() => {
    if (isAuthPage) return;
    const search = window.location.search;
    const returnTo = search ? `${pathname}${search}` : pathname;
    setHref(`/signup?next=${encodeURIComponent(returnTo)}`);
  }, [pathname, isAuthPage]);

  return (
    <Link href={href} className={className}>
      {children}
    </Link>
  );
}
